import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  getUsersApi,
  getUserApi,
  createUserApi,
  updateUserApi,
  changeUserPasswordApi,
  deleteUserApi,
} from "../../../api/auth/auth.api";

const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /*
  |--------------------------------------------------------------------------
  | GET USERS
  |--------------------------------------------------------------------------
  */

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getUsersApi();

      console.log("GET /auth/users response:", response);

      const userList = Array.isArray(response?.data)
        ? response.data
        : [];

      setUsers(userList);

      return userList;
    } catch (error) {
      console.error("Failed to fetch users:", error);

      setError(error);
      setUsers([]);

      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /*
  |--------------------------------------------------------------------------
  | LOAD USERS WHEN HOOK IS INITIALIZED
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /*
  |--------------------------------------------------------------------------
  | GET SINGLE USER
  |--------------------------------------------------------------------------
  */

  const fetchUser = useCallback(async (id) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getUserApi(id);

      return response.data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /*
  |--------------------------------------------------------------------------
  | CREATE USER
  |--------------------------------------------------------------------------
  */

  const createUser = useCallback(async (data) => {
    setLoading(true);
    setError(null);

    try {
      const response = await createUserApi(data);

      const createdUser = response.data;

      setUsers((currentUsers) => [
        createdUser,
        ...currentUsers,
      ]);

      return createdUser;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /*
  |--------------------------------------------------------------------------
  | UPDATE USER
  |--------------------------------------------------------------------------
  */

  const updateUser = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);

    try {
      const response = await updateUserApi(id, data);

      const updatedUser = response.data;

      setUsers((currentUsers) =>
        currentUsers.map((user) =>
          String(user.id) === String(id)
            ? updatedUser
            : user
        )
      );

      return updatedUser;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /*
  |--------------------------------------------------------------------------
  | CHANGE PASSWORD
  |--------------------------------------------------------------------------
  */

  const changePassword = useCallback(
    async (id, newPassword) => {
      setLoading(true);
      setError(null);

      try {
        const response =
          await changeUserPasswordApi(
            id,
            {
              newPassword,
            }
          );

        return response.data;
      } catch (error) {
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /*
  |--------------------------------------------------------------------------
  | DELETE / DEACTIVATE USER
  |--------------------------------------------------------------------------
  */

  const deleteUser = useCallback(async (id) => {
    setLoading(true);
    setError(null);

    try {
      const response = await deleteUserApi(id);

      const deletedUser = response.data;

      setUsers((currentUsers) =>
        currentUsers.map((user) =>
          String(user.id) === String(id)
            ? {
                ...user,
                status: deletedUser.status,
              }
            : user
        )
      );

      return deletedUser;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    users,
    loading,
    error,

    fetchUsers,
    fetchUser,

    createUser,
    updateUser,

    changePassword,

    deleteUser,

    clearError,
  };
};

export default useUsers;

export { useUsers };